import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import ExtremeTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/ExtremeTemperatureFaultChannel/ExtremeTemperatureFaultChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';

const isBaseStation = (
  deviceMacAddress,
  channelGroupMacAddress,
) => !!deviceMacAddress
  && !!channelGroupMacAddress
  && deviceMacAddress === channelGroupMacAddress;

const resolveChannelKey = (channelKeyOrString) => {
  if (!channelKeyOrString) return null;
  if (typeof channelKeyOrString === 'string') return new ChannelKey(channelKeyOrString);
  // If it walks like a ChannelKey (has the parsed fields), use as-is.
  return channelKeyOrString;
};

const joinSourceParts = (parts) => parts
  .map((p) => (p == null ? '' : String(p).trim()))
  .filter((p) => p.length > 0)
  .join(' | ');

export const getMaxComponentLabel = (deviceSensorList, raaChannelKey) => {
  const { macAddress, channelGroupMacAddress } = raaChannelKey;
  if (!deviceSensorList || !channelGroupMacAddress) {
    return '';
  }
  if (isBaseStation(macAddress, channelGroupMacAddress)) {
    return 'Base Station Component';
  }
  const sensors = deviceSensorList
    .filter((sensor) => sensor.channelGroupMacAddress === channelGroupMacAddress);
  const component = sensors
    .find((sensor) => sensor.channelGroupTypeId === ChannelGroupType.BLUETOOTH_LE.id);
  return component ? component.label : '';
};

export const getAlertSource = (alert, channel, isMaxAlert, componentLabel = '') => {
  const channelKey = resolveChannelKey(alert.channelKey);
  const macAddress = channelKey ? channelKey.macAddress : undefined;
  const channelGroupMacAddress = channelKey ? channelKey.channelGroupMacAddress : undefined;
  const channelGroupType = channelKey ? channelKey.channelGroupType : undefined;

  const channelDisplayName = (channel && channel.displayName) || '';

  if (isMaxAlert) {
    let sensorType;

    if (isBaseStation(macAddress, channelGroupMacAddress)) {
      sensorType = Number(channelGroupType) === ChannelGroupType.TEMPERATURE.id
        ? 'Internal Temperature'
        : 'Device';
    } else if (Number(channelGroupType) === ChannelGroupType.BLUETOOTH_LE.id) {
      sensorType = 'Device';
    } else {
      const groupTypeInfo = ChannelService.getChannelGroupTypeByValueId(Number(channelGroupType));
      sensorType = alert.channelGroupName
        || (groupTypeInfo && groupTypeInfo.displayName)
        || '';
    }

    return joinSourceParts([alert.deviceName, componentLabel, sensorType, channelDisplayName]);
  }

  // Non-MAX: fall back to channelGroupSourceName when channelGroupName is null
  // (matches what the history endpoint returns for base-station channels).
  const groupLabel = alert.channelGroupName || alert.channelGroupSourceName;
  return joinSourceParts([alert.deviceName, groupLabel, channelDisplayName]);
};

// ---------------------------------------------------------------------------
// Special-channel helpers
// ---------------------------------------------------------------------------

const SPECIAL_CHANNEL_TYPES = [
  BooleanChannel,
  RaaExtremeTempFaultChannel,
  ExtremeTemperatureFaultChannel,
  IntegerAsBooleanChannel,
];

export const isSpecialChannel = (channel) => (
  SPECIAL_CHANNEL_TYPES.some((c) => channel instanceof c)
);

export const getSpecialConditionLabel = (channel, alert, isTrigger, operatorTypeId) => {
  if (channel instanceof BooleanChannel
    && !(channel instanceof ExtremeTemperatureFaultChannel)
    && !(channel instanceof RaaExtremeTempFaultChannel)) {
    return channel.getDisplayValue(isTrigger ? alert.startThreshold : !alert.startThreshold);
  }
  if (channel instanceof ExtremeTemperatureFaultChannel
    || channel instanceof RaaExtremeTempFaultChannel
    || channel instanceof IntegerAsBooleanChannel) {
    const resolvedOperatorTypeId = operatorTypeId != null
      ? operatorTypeId
      : alert.startOperatorTypeId;
    const isFaultTriggerCondition = alert.startThreshold === 1 && resolvedOperatorTypeId === 3;
    if (isTrigger) return isFaultTriggerCondition ? 'No Fault' : 'Fault';
    return isFaultTriggerCondition ? 'Fault' : 'No Fault';
  }
  return '';
};
